import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Mail, Check, Clock, Building2, Phone } from 'lucide-react';
import { contactAPI } from '@/lib/api';
import { toast } from 'sonner';

export default function AdminContacts() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    try {
      const response = await contactAPI.getAll();
      setContacts(response.data);
    } catch (error) {
      toast.error('Failed to load inquiries');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkRead = async (id) => {
    try {
      await contactAPI.markRead(id);
      setContacts(contacts.map(c => c.id === id ? { ...c, is_read: true } : c));
      toast.success('Marked as read');
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const serviceLabels = {
    accounting: 'Accounting & Bookkeeping',
    tax: 'Tax & Compliance',
    registration: 'Registration Services',
    cfo: 'Fractional CFO',
    'cxo-hiring': 'CXO Hiring',
    other: 'Other',
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-screen">
        <div className="spinner"></div>
      </div>
    );
  }

  const unreadContacts = contacts.filter(c => !c.is_read);
  const readContacts = contacts.filter(c => c.is_read);

  return (
    <div className="p-6 lg:p-8" data-testid="admin-contacts-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-slate-900">Inquiries</h1>
        <p className="text-slate-600">{contacts.length} total inquiries ({unreadContacts.length} new)</p>
      </div>

      {contacts.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Mail className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">No inquiries yet</h3>
            <p className="text-slate-500">
              When visitors submit the contact form, their inquiries will appear here.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {/* Unread */}
          {unreadContacts.length > 0 && (
            <div>
              <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-[#E84C3D]"></span>
                New Inquiries ({unreadContacts.length})
              </h2>
              <div className="space-y-4">
                {unreadContacts.map((contact) => (
                  <Card key={contact.id} className="border-l-4 border-l-[#E84C3D]" data-testid={`contact-${contact.id}`}>
                    <CardContent className="p-6">
                      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-3">
                            <h3 className="font-semibold text-slate-900 text-lg">{contact.name}</h3>
                            <span className="px-3 py-1 bg-[#E84C3D]/10 text-[#E84C3D] text-xs font-medium rounded-full">
                              {serviceLabels[contact.service_interest] || contact.service_interest}
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-4 text-sm text-slate-600 mb-4">
                            <span className="flex items-center gap-1">
                              <Mail size={14} />
                              {contact.email}
                            </span>
                            {contact.phone && (
                              <span className="flex items-center gap-1">
                                <Phone size={14} />
                                {contact.phone}
                              </span>
                            )}
                            {contact.company && (
                              <span className="flex items-center gap-1">
                                <Building2 size={14} />
                                {contact.company}
                              </span>
                            )}
                          </div>
                          <p className="text-slate-700 bg-slate-50 p-4 rounded-lg">{contact.message}</p>
                          <p className="text-xs text-slate-400 mt-3 flex items-center gap-1">
                            <Clock size={12} />
                            {formatDate(contact.submitted_at)}
                          </p>
                        </div>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleMarkRead(contact.id)}
                          data-testid={`mark-read-${contact.id}`}
                        >
                          <Check size={16} className="mr-1" />
                          Mark Read
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Read */}
          {readContacts.length > 0 && (
            <div>
              <h2 className="text-lg font-semibold text-slate-900 mb-4">
                Previous Inquiries ({readContacts.length})
              </h2>
              <div className="space-y-4">
                {readContacts.map((contact) => (
                  <Card key={contact.id} className="opacity-70" data-testid={`contact-${contact.id}`}>
                    <CardContent className="p-6">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-3">
                          <h3 className="font-semibold text-slate-900">{contact.name}</h3>
                          <span className="px-3 py-1 bg-slate-100 text-slate-600 text-xs font-medium rounded-full">
                            {serviceLabels[contact.service_interest] || contact.service_interest}
                          </span>
                          <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full flex items-center gap-1">
                            <Check size={10} />
                            Read
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-4 text-sm text-slate-600 mb-4">
                          <span className="flex items-center gap-1">
                            <Mail size={14} />
                            {contact.email}
                          </span>
                          {contact.phone && (
                            <span className="flex items-center gap-1">
                              <Phone size={14} />
                              {contact.phone}
                            </span>
                          )}
                          {contact.company && (
                            <span className="flex items-center gap-1">
                              <Building2 size={14} />
                              {contact.company}
                            </span>
                          )}
                        </div>
                        <p className="text-slate-600 text-sm">{contact.message}</p>
                        <p className="text-xs text-slate-400 mt-3 flex items-center gap-1">
                          <Clock size={12} />
                          {formatDate(contact.submitted_at)}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
